import { useCallback } from "react";
import { type NavigateOptions, type To, useNavigate } from "react-router-dom-v5-compat";

import { useWebviewContext } from "@btnet/utilities-webview";

/**
 * Navigation hook for tab-style lateral view switches.
 *
 * In webview context, defaults to `replace: true` so tab switches don't stack
 * on the native back-stack and feel like forward drilling. On the web, defaults
 * to push so browser back navigates between tabs as users expect.
 *
 * Why: ADO 271879 — route-based tabs (BdsButtonGroup, etc.) were pushing onto
 * the React Native nav stack and breaking the lateral-switching mental model.
 *
 * Callers may override either default by passing `replace` in options.
 *
 * @example
 * ```tsx
 * const tabNavigate = useTabNavigate();
 * tabNavigate(routes.timeClock.getListLink(ShiftListContainerTabs.Reports));
 * ```
 */
export function useTabNavigate() {
    const navigate = useNavigate();
    const isWebview = useWebviewContext() !== undefined;

    return useCallback(
        (to: To, options?: NavigateOptions) => {
            navigate(to, { replace: isWebview, ...options });
        },
        [navigate, isWebview],
    );
}
